import { useEffect, useState } from "react";
import { useI18n } from "../i18n/LanguageContext";
import { api, formatApiErrorDetail } from "../lib/api";
import BrandMark from "./BrandMark";

/**
 * "Forgot password" modal — public-facing entry to the recovery flow.
 *
 * Submitting the email triggers POST /api/auth/forgot-password which:
 *   1. emails compliance@ (+ clients@ CC) with caller fingerprint;
 *   2. emails the client an acknowledgment.
 * The server always returns 204 to avoid leaking email existence.
 *
 * The white-glove copy is preserved below the form: voice verification
 * is still the canonical path before any reset token is issued.
 */
export default function ForgotPasswordModal({ open, onClose }) {
  const { t, locale } = useI18n();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open) {
      setEmail("");
      setSent(false);
      setError("");
      setSubmitting(false);
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    setError("");
    try {
      await api.post("/auth/forgot-password", { email: email.trim(), locale });
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message || t("forgot.error_generic"));
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-6 py-10 overflow-y-auto"
      style={{ background: "rgba(10,14,26,0.78)", backdropFilter: "blur(8px)" }}
      onClick={onClose}
      data-testid="forgot-password-modal"
    >
      <div
        className="br-card br-aurora-ribbon relative max-w-[520px] w-full px-9 py-10 br-fade-up"
        onClick={(e) => e.stopPropagation()}
        data-testid="forgot-password-modal-card"
      >
        <button
          onClick={onClose}
          className="absolute top-5 right-6 font-mono-fin text-[0.7rem] tracking-[0.2em] uppercase text-[color:var(--br-text-dim)] hover:text-[color:var(--br-bronze-soft)] transition-colors"
          aria-label={t("forgot.close")}
          data-testid="forgot-password-modal-close"
        >
          ✕ {t("forgot.close")}
        </button>

        <div className="flex items-center gap-4 mb-6">
          <BrandMark size={40} />
          <div>
            <div className="br-overline">{t("forgot.overline")}</div>
            <div className="font-serif-display italic text-[1.2rem] text-[color:var(--br-parchment)] mt-1 leading-none">
              {t("forgot.title_a")} <span className="text-[color:var(--br-bronze-soft)]">{t("forgot.title_em")}</span>
            </div>
          </div>
        </div>

        <h2 className="font-serif-display text-[1.9rem] leading-[1.15]">
          {t("forgot.heading_a")} <em>{t("forgot.heading_em")}</em>
          {t("forgot.heading_b")}
        </h2>
        <p className="text-[0.95rem] text-[color:var(--br-text)] mt-4 leading-relaxed">
          {t("forgot.p1")}
        </p>

        {/* Form / confirmation */}
        {sent ? (
          <div
            className="mt-7 p-5 border-l-2 br-fade-up"
            style={{ borderColor: "var(--br-bronze)", background: "rgba(167,128,73,0.06)" }}
            data-testid="forgot-password-confirmation"
          >
            <div className="br-overline">{t("forgot.confirmation_overline")}</div>
            <p className="mt-3 text-[0.95rem] text-[color:var(--br-text)] leading-relaxed">
              {t("forgot.confirmation_p1")}
            </p>
            <p className="mt-3 text-[0.85rem] text-[color:var(--br-text-dim)] leading-relaxed">
              {t("forgot.confirmation_p2")}
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-7 space-y-3" data-testid="forgot-password-form">
            <label className="block">
              <span className="br-overline">{t("forgot.form_email_label")}</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                placeholder="you@example.com"
                className="br-input mt-2"
                data-testid="forgot-password-email-input"
              />
            </label>
            {error && (
              <div
                className="text-[0.85rem] py-2 px-3 border-l-2"
                style={{ borderColor: "var(--br-red)", color: "var(--br-red)" }}
                data-testid="forgot-password-error"
              >
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={submitting || !email.trim()}
              className="br-btn-primary w-full"
              data-testid="forgot-password-submit"
            >
              {submitting ? t("forgot.form_submitting") : t("forgot.form_submit")}
            </button>
          </form>
        )}

        {/* Process explanation — preserved from the original white-glove copy */}
        <div
          className="mt-7 p-5 border-l-2"
          style={{ borderColor: "var(--br-bronze)", background: "rgba(167,128,73,0.04)" }}
        >
          <div className="br-overline">{t("forgot.how_overline")}</div>
          <ol className="mt-3 space-y-2 text-[0.92rem] text-[color:var(--br-text)] list-decimal list-inside">
            <li>{t("forgot.step1")}</li>
            <li>{t("forgot.step2")}</li>
            <li>{t("forgot.step3")}</li>
          </ol>
        </div>

        <div className="mt-6 pt-5 border-t border-[color:var(--br-line)] text-[0.72rem] text-[color:var(--br-text-dim)] leading-relaxed">
          <span className="font-mono-fin tracking-[0.16em] uppercase text-[color:var(--br-bronze)]">
            {t("forgot.reminder_title")}
          </span>{" "}
          · {t("forgot.reminder_body")}
        </div>
      </div>
    </div>
  );
}
